// ==========================================
// src/components/common/Navbar/Navbar.tsx
// ==========================================
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import styled from 'styled-components';
import { Menu, X, Phone, Mail } from 'lucide-react';
import { config } from '../../../config';

const NavbarContainer = styled(motion.nav)<{ scrolled: boolean }>`
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 1000;
  background: ${props => props.scrolled ? 'rgba(255, 255, 255, 0.95)' : 'transparent'};
  backdrop-filter: ${props => props.scrolled ? 'blur(10px)' : 'none'};
  box-shadow: ${props => props.scrolled ? props.theme.shadows.medium : 'none'};
  transition: all 0.3s ease;
`;

const NavWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem;
  max-width: 1400px;
  margin: 0 auto;

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    padding: 1rem;
  }
`;

const Logo = styled(Link)<{ scrolled: boolean }>`
  font-family: ${props => props.theme.fonts.primary};
  font-size: 2rem;
  font-weight: 900;
  color: ${props => props.scrolled ? props.theme.colors.primary : props.theme.colors.white};
  letter-spacing: 2px;
  text-shadow: ${props => !props.scrolled ? '2px 2px 4px rgba(0,0,0,0.3)' : 'none'};
  transition: all 0.3s ease;

  &:hover {
    color: ${props => props.theme.colors.secondary};
  }
`;

const NavMenu = styled.ul<{ isOpen: boolean }>`
  display: flex;
  list-style: none;
  gap: 2.5rem;
  align-items: center;

  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    position: fixed;
    top: 0;
    right: ${props => props.isOpen ? '0' : '-100%'};
    height: 100vh;
    width: 100%;
    max-width: 400px;
    background: white;
    flex-direction: column;
    justify-content: center;
    gap: 2rem;
    transition: right 0.3s ease;
    box-shadow: -5px 0 15px rgba(0,0,0,0.1);
  }
`;

const NavLink = styled(Link)<{ scrolled: boolean; active: boolean }>`
  font-weight: 500;
  color: ${props => props.scrolled ? props.theme.colors.dark : props.theme.colors.white};
  position: relative;
  text-shadow: ${props => !props.scrolled ? '1px 1px 2px rgba(0,0,0,0.3)' : 'none'};

  &::after {
    content: '';
    position: absolute;
    bottom: -5px;
    left: 0;
    width: ${props => props.active ? '100%' : '0'};
    height: 2px;
    background: ${props => props.theme.colors.secondary};
    transition: width 0.3s ease;
  }

  &:hover::after {
    width: 100%;
  }

  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    color: ${props => props.theme.colors.dark};
    font-size: 1.25rem;
    text-shadow: none;
  }
`;

const ContactInfo = styled.div<{ scrolled: boolean }>`
  display: flex;
  gap: 1.5rem;
  align-items: center;

  @media (max-width: ${props => props.theme.breakpoints.xl}) {
    display: none;
  }

  a {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: ${props => props.scrolled ? props.theme.colors.primary : props.theme.colors.white};
    text-shadow: ${props => !props.scrolled ? '1px 1px 2px rgba(0,0,0,0.3)' : 'none'};
    font-size: 0.9rem;

    &:hover {
      color: ${props => props.theme.colors.secondary};
    }
  }
`;

const MenuToggle = styled.button<{ scrolled: boolean }>`
  display: none;
  background: none;
  border: none;
  color: ${props => props.scrolled ? props.theme.colors.dark : props.theme.colors.white};
  font-size: 1.5rem;
  z-index: 1001;

  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const Overlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
`;

const BookingButton = styled.a<{ scrolled: boolean }>`
  padding: 12px 28px;
  background: linear-gradient(135deg, 
    ${props => props.theme.colors.secondary} 0%, 
    ${props => props.theme.colors.accent} 100%
  );
  color: white;
  border-radius: 50px;
  font-weight: 600;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  white-space: nowrap;
  box-shadow: 0 4px 15px rgba(212, 165, 116, 0.3);
  text-decoration: none;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(212, 165, 116, 0.4);
  }

  @media (max-width: ${props => props.theme.breakpoints.xl}) {
    display: none;
  }
`;

const Navbar: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  const navItems = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About' },
    { path: '/rooms', label: 'Rooms' },
    { path: '/amenities', label: 'Amenities' },
    { path: '/attractions', label: 'Attractions' },
    { path: '/contact', label: 'Contact' },
  ];

  return (
    <>
      <NavbarContainer
        scrolled={scrolled}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <NavWrapper>
          <Logo to="/" scrolled={scrolled}>
            NYSDILL
          </Logo>

          <NavMenu isOpen={isMenuOpen}>
            {navItems.map((item) => (
              <li key={item.path}>
                <NavLink
                  to={item.path}
                  scrolled={scrolled}
                  active={location.pathname === item.path}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.label}
                </NavLink>
              </li>
            ))}
          </NavMenu>

          <ContactInfo scrolled={scrolled}>
            <a href="tel:+441603733341">
              <Phone size={16} />
              +44 1603 733341
            </a>
            <a href="mailto:hello@nysdill.com">
              <Mail size={16} />
              Email Us
            </a>
          </ContactInfo>

          <BookingButton 
            href="https://booknorwich.nysdill.com" 
            target="_blank" 
            rel="noopener noreferrer"
            scrolled={scrolled}
          >
            Book Now
          </BookingButton>

          <MenuToggle
            scrolled={scrolled}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </MenuToggle>
        </NavWrapper>
      </NavbarContainer>

      <AnimatePresence>
        {isMenuOpen && (
          <Overlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsMenuOpen(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;